import type { BonusAction, TrialStatus } from '../../types/membership';
import { readJSON, writeJSON, delay } from './storage';
import { tokenService } from './tokenService';

const KEY = 'teskedo_trial_v1';
const TRIAL_DAYS = 21;
const TRIAL_TOKENS = 60;
const MAX_BONUS_TOKENS = 190;

export const BONUS_ACTIONS: BonusAction[] = [
  { id: 'goals_survey', label: 'Complete the goals survey', tokens: 30 },
  { id: 'watch_videos', label: 'Watch the getting-started videos', tokens: 20 },
  { id: 'invite_friend', label: 'Invite a friend', tokens: 40 },
  { id: 'connect_linkedin', label: 'Connect your LinkedIn', tokens: 50 },
  { id: 'share_social', label: 'Share Teskedo on social', tokens: 50 },
];

const emptyStatus: TrialStatus = {
  isActive: false,
  startDate: null,
  endDate: null,
  totalBonusTokensEarned: 0,
  completedActions: [],
};

function read(): TrialStatus {
  return readJSON<TrialStatus>(KEY, emptyStatus);
}

export const trialService = {
  getStatus(): TrialStatus {
    const status = read();
    // Expire the trial automatically once the end date passes.
    if (status.isActive && status.endDate && new Date(status.endDate).getTime() < Date.now()) {
      const expired = { ...status, isActive: false };
      writeJSON(KEY, expired);
      return expired;
    }
    return status;
  },

  isEligible(): boolean {
    return read().startDate === null;
  },

  async startTrial(): Promise<TrialStatus> {
    const existing = read();
    if (existing.startDate) return delay(this.getStatus());

    const now = new Date();
    const end = new Date(now.getTime() + TRIAL_DAYS * 86400000);
    const status: TrialStatus = {
      isActive: true,
      startDate: now.toISOString(),
      endDate: end.toISOString(),
      totalBonusTokensEarned: 0,
      completedActions: [],
    };
    writeJSON(KEY, status);
    await tokenService.earn(TRIAL_TOKENS, 'trial', 'Free trial starter tokens', TRIAL_DAYS);
    return delay(status);
  },

  /** Records a one-time bonus action, capped at MAX_BONUS_TOKENS total. */
  async recordBonusAction(actionId: string): Promise<TrialStatus> {
    const action = BONUS_ACTIONS.find((a) => a.id === actionId);
    const status = read();
    if (!action || status.completedActions.includes(actionId)) return delay(status);

    const remaining = MAX_BONUS_TOKENS - status.totalBonusTokensEarned;
    const award = Math.max(0, Math.min(action.tokens, remaining));

    const next: TrialStatus = {
      ...status,
      completedActions: [...status.completedActions, actionId],
      totalBonusTokensEarned: status.totalBonusTokensEarned + award,
    };
    writeJSON(KEY, next);
    if (award > 0) {
      await tokenService.earn(award, 'bonus_action', `Trial bonus: ${action.label}`, TRIAL_DAYS);
    }
    return delay(next);
  },

  maxBonusTokens(): number {
    return MAX_BONUS_TOKENS;
  },

  daysRemaining(): number {
    const { endDate } = this.getStatus();
    if (!endDate) return 0;
    return Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000));
  },
};
